import React, {useCallback, useMemo} from 'react';
import SecurSpaceModal from '../../common/SecurSpaceModal';
import {AiOutlineClose} from 'react-icons/ai';
import {HashNavigation, Navigation, Pagination} from 'swiper/modules';
import {SwiperSlide} from 'swiper/react';
import {MainSwiper} from '../../location/profile/carouselStyle';
import Image from '../../skeleton/ImageComponent';
import '../../location/profile/style.css';
import {constructInterchangeGalleryImageUrl} from "../../../util/ConstructImageUrlUtil";
import {InventoryGallery} from "../../../types/model/orm/InventoryGallery";

const VIDEO_EXTENSIONS = ['.mp4', '.webm', '.ogg', '.mov'];

const isVideoUrl = (url: string): boolean => {
  try {
    const pathname = new URL(url).pathname.toLowerCase();
    return VIDEO_EXTENSIONS.some((ext) => pathname.endsWith(ext));
  } catch {
    return VIDEO_EXTENSIONS.some((ext) => url.toLowerCase().endsWith(ext));
  }
};

interface InterchangeImageGalleryProps {
  interchangeId: string;
  inventoryGallery: InventoryGallery[];
  isOpen: boolean;
  onClose: () => void;
}

const InterchangeImageGallery: React.FC<InterchangeImageGalleryProps> = ({
  interchangeId,
  inventoryGallery,
  isOpen,
  onClose
}) => {
  const galleryMediaUrls = useMemo(() => {
    if (!inventoryGallery || inventoryGallery.length === 0) {
      return [];
    }
    return inventoryGallery
      .map((item) => constructInterchangeGalleryImageUrl(interchangeId, item.galleryImageFileName))
      .filter(Boolean);
  }, [interchangeId, inventoryGallery]);

  const handleSlideChange = useCallback((swiper: { slides: HTMLElement[]; activeIndex: number }) => {
    swiper.slides.forEach((slide) => {
      slide.querySelectorAll('video').forEach((video) => video.pause());
    });
    const activeSlide = swiper.slides[swiper.activeIndex];
    if (activeSlide) {
      activeSlide.querySelectorAll('video').forEach((video) => {
        video.play().catch(() => {});
      });
    }
  }, []);


  if (galleryMediaUrls.length === 0) {
    return null;
  }

  return (
    <SecurSpaceModal overlayClassName="ss-modal-center" className="ss-gallery-modal" isOpen={isOpen}>
      <div className='ss-gallery-modal-container'>
        <button onClick={onClose} className='ss-modal-close-btn' aria-label="Close media gallery">
          <AiOutlineClose />
        </button>
        {galleryMediaUrls.length > 0 && (
          <MainSwiper
            loop={true}
            navigation={true}
            slidesPerView={1}
            hashNavigation={{ watchState: true }}
            pagination={{ clickable: true }}
            onSlideChange={handleSlideChange}
            breakpoints={{
              400: {
                slidesPerView: 1,
                autoplay: false,
              },
            }}
            modules={[HashNavigation, Navigation, Pagination]}
          >
            {galleryMediaUrls.map((url, index) => (
              <SwiperSlide key={url}>
                {isVideoUrl(url) ? (
                  <video
                    controls
                    style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                    aria-label={`gallery-video ${index + 1}`}
                  >
                    <source src={url} />
                    Your browser does not support video playback.
                  </video>
                ) : (
                  <Image
                    errorImg='/app-images/not-found-image/place_holder_image.png'
                    placeHolderimg="/app-images/loading/image-loader.gif"
                    src={url}
                    alt={`gallery-item ${index + 1}`}
                    height="100%"
                    width="100%"
                  />
                )}
              </SwiperSlide>
            ))}
          </MainSwiper>
        )}
      </div>
    </SecurSpaceModal>
  );
};

export default InterchangeImageGallery;

